import {
  ProductReportFilters,
  type ProductReportFilters as ProductReportFiltersType,
} from "@/components/reports/ProductReportFilters";
import { ReportTable } from "@/components/reports/ReportTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  GetOptions,
  ProductReport,
  QueryConstraint,
  Report,
  ReportType,
} from "@/core";
import { useProductReportsPaginated } from "@/hooks";
import { useGetProducts } from "@/hooks/repository-hooks/product/use-product";
import { functionsService } from "@/services/functions/functions-service";
import { useInitializeProducts } from "@/stores/products";
import {
  applySortingToReports,
  exportProductReportsToCsv,
  SortingState,
} from "@/utils/csv-export";
import { formatNumber } from "@/utils/formatting";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { BarChart3, Download, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateRange } from "react-day-picker";

export default function ProductReportsPage() {
  const [filters, setFilters] = useState<ProductReportFiltersType>({
    searchTerm: "",
    dateRange: undefined,
    productIds: [],
    transactionType: "both",
  });
  const [timeRangeReports, setTimeRangeReports] = useState<Report[] | null>(
    null,
  );
  const [isTimeRangeLoading, setIsTimeRangeLoading] = useState(false);
  const [tableSorting, setTableSorting] = useState<SortingState[]>([]);
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef(filters);

  // Keep filtersRef in sync with filters
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const queryClient = useQueryClient();
  useInitializeProducts();

  // The search box filters locally, so a paged query would only ever match the
  // rows already loaded. Widen the page while searching so every report is
  // reachable without hammering "Load More".
  const pageSize = filters.searchTerm.trim() ? 10_000 : 100;

  const queryOptions: GetOptions = useMemo(() => {
    const constraints: QueryConstraint[] = [
      { field: "type", operator: "==", value: ReportType.PRODUCT },
    ];

    // Product filtering is done on the frontend to avoid unnecessary API calls
    // when selecting/unselecting products

    constraints.push({
      field: "buyOrSell",
      operator: "==",
      value: filters.transactionType,
    });

    if (filters.dateRange?.from) {
      constraints.push({
        field: "dataAsOf",
        operator: ">=",
        value: filters.dateRange.from,
      });
    }
    if (filters.dateRange?.to) {
      constraints.push({
        field: "dataAsOf",
        operator: "<=",
        value: filters.dateRange.to,
      });
    }

    return {
      queryConstraints: constraints,
      pagination: { limit: pageSize },
      orderBy: { field: "dataAsOf", direction: "desc" },
    };
  }, [filters.transactionType, filters.dateRange, pageSize]);

  const productData = useProductReportsPaginated(queryOptions);
  console.log(productData.error);
  const reports = useMemo(
    () => (productData.data ? productData.data.pages.flat() : []),
    [productData.data],
  );

  const { data: productsData } = useGetProducts();

  const filteredReports = useMemo(() => {
    // If there is a date range selected, always use time range reports (from API call)
    if (filters.dateRange?.from && filters.dateRange?.to && timeRangeReports) {
      return timeRangeReports.filter((report: Report) => {
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const productName =
            report.type === ReportType.PRODUCT && "productName" in report
              ? report.productName
              : "";
          const matchesSearch =
            report.id.toLowerCase().includes(searchLower) ||
            productName.toLowerCase().includes(searchLower) ||
            report.type.toLowerCase().includes(searchLower);

          if (!matchesSearch) return false;
        }

        // Filter by selected products on the frontend
        if (filters.productIds.length > 0) {
          const productId =
            report.type === ReportType.PRODUCT && "productId" in report
              ? report.productId
              : "";
          if (!filters.productIds.includes(productId)) {
            return false;
          }
        }


        return true;
      });
    }

    // Otherwise use repository data with filters (when no date range is selected)
    if (!reports) return [];

    return reports.filter((report: ProductReport) => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          report.id.toLowerCase().includes(searchLower) ||
          report.productName?.toLowerCase().includes(searchLower) ||
          report.type.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      // Filter by selected products
      if (filters.productIds.length > 0) {
        if (!filters.productIds.includes(report.productId)) {
          return false;
        }
      }

      // Filter by transaction type (always exact match)
      if (report.buyOrSell !== filters.transactionType) {
        return false;
      }

      return true;
    });
  }, [reports, filters, timeRangeReports]);

  const handleRefresh = async () => {
    try {
      await productData.refetch();
    } catch (error) {
      console.error("Failed to refresh reports:", error);
    }
  };

  const handleDateRangeChange = async (newDateRange: DateRange | undefined) => {
    setFilters((prev) => ({ ...prev, dateRange: newDateRange }));

    if (!newDateRange?.from || !newDateRange?.to) {
      // Clear time range reports when date filter is cleared
      setTimeRangeReports(null);
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
        fetchDebounceRef.current = null;
      }
      return;
    }
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }
    fetchDebounceRef.current = setTimeout(() => {
      void handleGetProductReportByTimeRange(newDateRange);
    }, 2000);
  };

  const handleGetProductReportByTimeRange = useCallback(
    async (dateRangeParam?: DateRange, transactionType?: "buy" | "sell" | "both") => {
      const targetDateRange = dateRangeParam || filtersRef.current.dateRange;
      const targetTransactionType = transactionType || filtersRef.current.transactionType;

      if (!targetDateRange?.from || !targetDateRange?.to) {
        console.error("Date range is required");
        return;
      }

      setIsTimeRangeLoading(true);
      try {
        const payload = {
          productId: "*",
          startTime: format(targetDateRange.from, 'yyyy-MM-dd'),
          endTime: format(targetDateRange.to, 'yyyy-MM-dd'),
          ...(targetTransactionType !== "both"
            ? { buyOrSell: targetTransactionType }
            : {}),
        };

        const data =
          await functionsService.getProductReportByTimeRange(payload);

        console.log("handleGetProductReportByTimeRange data: ", data);
        // Transform ReportData[] to Report[] by adding missing base entity fields
        const transformedReports = (data || []).map((reportData, index) => ({
          ...reportData,
          id: `temp-${Date.now()}-${index}`,
          createdAt: new Date(),
          updatedAt: new Date(),
        }));
        setTimeRangeReports(transformedReports);

        // Invalidate the base reports query to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ["reports", "product"] });
      } catch (error) {
        console.error("Error fetching product report by time range:", error);
        setTimeRangeReports([]);
      } finally {
        setIsTimeRangeLoading(false);
      }
    },
    [queryClient],
  );

  // Handle transaction type changes - re-fetch when date range is active
  useEffect(() => {
    if (!filters.dateRange?.from || !filters.dateRange?.to) return;
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }
    fetchDebounceRef.current = setTimeout(() => {
      void handleGetProductReportByTimeRange(undefined, filters.transactionType);
    }, 2000);
  }, [
    filters.transactionType,
    filters.dateRange?.from,
    filters.dateRange?.to,
    handleGetProductReportByTimeRange,
  ]);

  useEffect(() => {
    return () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
    };
  }, []);

  const handleFiltersChange = (newFilters: ProductReportFiltersType) => {
    setFilters(newFilters);

    // If date range changed, handle the API call
    if (newFilters.dateRange !== filters.dateRange) {
      handleDateRangeChange(newFilters.dateRange);
    }
  };

  const handleExport = () => {
    if (filteredReports.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Apply the same sorting as the table before exporting
    const sortedReports = applySortingToReports(
      filteredReports as ProductReport[],
      tableSorting,
    );
    exportProductReportsToCsv(sortedReports, filters.transactionType);
  };

  if ((productData.error || !reports) && !productData.isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Product Reports
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              There was an error loading the product reports data. Please try
              again.
            </p>
            <Button onClick={handleRefresh} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold">Product Reports</h2>
          <h3 className="text-lg ml-2 text-muted-foreground self-start mt-1">
            ({formatNumber(filteredReports?.length || 0)})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={
              productData.isLoading ||
              productData.isFetching ||
              isTimeRangeLoading ||
              filteredReports.length === 0
            }
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={productData.isLoading || productData.isFetching}
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                productData.isLoading || productData.isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <ProductReportFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        products={productsData || []}
      />

      {/* Reports Table */}
      <ReportTable
        key="ReportTable-PRODUCT"
        reports={filteredReports}
        isLoading={
          filters.dateRange?.from && filters.dateRange?.to
            ? isTimeRangeLoading
            : productData.isLoading || (productData.isFetching && reports.length === 0)
        }
        selectedReportType={ReportType.PRODUCT}
        onSortingChange={setTableSorting}
      />

      {!timeRangeReports && productData.hasNextPage && (
        <div className="flex items-center justify-center py-4">
          <Button
            variant="outline"
            onClick={() => productData.fetchNextPage()}
            disabled={productData.isFetchingNextPage}
          >
            {productData.isFetchingNextPage ? (
              <>
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                Loading more...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
